import React, { FC, useState, useEffect } from 'react'
import { ScrollView, TouchableOpacity, View, Text, Image } from 'react-native'
import { useNavigate, useParams } from 'react-router-dom'
import { travelApi, TravelLogic, type ITravelData } from '@front/logic'
import { getHomeInfo } from '@front/apis'

import {
  AppBackground,
  BottomActionContainer,
  Button,
  ResponsiveLogo,
} from '@ui/components'

import {
  Headline2,
  Headline4,
  Headline5,
  BodyText,
  colors
} from '@/aena/styles' // Importando los estilos del PetAdd

import { ReactComponent as AenaDogIcon } from '@/assets/icons/aena/aena_dog.svg'
import { HeaderWithBackAena } from '@/components/headerwithback'
import { downloadJson, openStoredDocument, downloadDocument } from './docUtils'

// Etiqueta legible por tipo de documento del check-in.
const DOC_LABELS: Record<string, string> = {
  liability_release: 'Descargo de responsabilidad (F244)',
  full_report: 'Certificado PetPass',
  passport: 'Pasaporte digital',
  rabies_certificate: 'Vacunación',
}
const docLabel = (type: string): string => DOC_LABELS[type] || type
// Orden de prioridad para mostrarlos (descargo primero).
const DOC_ORDER = ['liability_release', 'full_report', 'passport', 'rabies_certificate']
// Tipos de documento que se ocultan: el pasaporte digital y la vacunación ya
// van incluidos dentro del Certificado PetPass, así que no se listan aparte.
const HIDDEN_DOC_TYPES = ['passport', 'rabies_certificate']

export const TravelDetail: FC = () => {
  const navigate = useNavigate()
  const { travelId } = useParams<{ travelId: string }>()

  const [travel, setTravel] = useState<ITravelData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [petAvatars, setPetAvatars] = useState<Record<string, string>>({})

  useEffect(() => {
    const loadTravelDetail = async () => {
      if (!travelId) return

      try {
        setLoading(true)
        setError(null)
        const response = await travelApi.getTravelDetail({ travelId })
        setTravel(response.data)
      } catch (err) {
        console.error('Error loading travel detail:', err)
        setError('Error al cargar los detalles del viaje')
      } finally {
        setLoading(false)
      }
    }

    const loadPetAvatars = async () => {
      try {
        const home = await getHomeInfo({ lat: 0, lon: 0 })
        const map: Record<string, string> = {}
        home.data?.petList?.forEach((p: any) => { if (p?.guid) map[p.guid] = p.avatar })
        setPetAvatars(map)
      } catch (e) {
        console.debug('Error loading pet avatars:', e)
      }
    }

    loadTravelDetail()
    loadPetAvatars()
  }, [travelId])

  // Usar el mismo mapeo que la lista (TravelLogic): pending->Pendiente,
  // confirmed->Validado (verde), completed, cancelled. Antes había un switch local
  // desfasado que devolvía "Desconocido" para pending/confirmed.
  const getStatusColor = (status: string) => TravelLogic.getStatusColor(status)
  const getStatusText = (status: string) => TravelLogic.getStatusText(status)

  const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <View style={{
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.grisClaro
    }}>
      <BodyText style={{ color: colors.grisOscuro, fontWeight: '500' }}>{label}</BodyText>
      <BodyText style={{ fontWeight: '600', textAlign: 'right', flex: 1, marginLeft: 16 }}>
        {value}
      </BodyText>
    </View>
  )

  const PetCard: React.FC<{ pet: ITravelData['pets'][0] }> = ({ pet }) => {
    const avatar = petAvatars[pet.guid]
    return (
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.grisClaro,
        padding: 12,
        borderRadius: 8,
        marginBottom: 8,
      }}>
        <View style={{ marginRight: 12 }}>
          {avatar ? (
            <Image source={{ uri: avatar }} style={{ width: 40, height: 40, borderRadius: 20 }} />
          ) : (
            <AenaDogIcon width={32} height={32} />
          )}
        </View>
        <View style={{ flex: 1 }}>
          <Headline4>
            {pet.name}
          </Headline4>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 2 }}>
            <BodyText style={{ color: colors.grisOscuro, fontSize: 14 }}>
              {pet.travelType}
            </BodyText>
          </View>
        </View>
      </View>
    )
  }

 
  const handleEdit = () => {
    if (travel) {
      navigate(`${travel.id}/edit`)
    }
  }

  const handleDelete = () => {
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    if (!travel) return

    try {
      await travelApi.deleteTravel(travel.id)
      setShowDeleteConfirm(false)
      navigate('/aena/travel')
    } catch (error) {
      console.error('Error deleting travel:', error)
      alert('Error al eliminar el viaje. Inténtalo de nuevo.')
    }
  }

  const handleViewDocuments = () => {
    if (travel) {
      navigate(`aena/travel/detail/${travel.id}/documents`)
    }
  }

  // Documentos visibles: se ocultan pasaporte y vacunación (su info ya está en el
  // Certificado PetPass) y se ordenan según DOC_ORDER (descargo primero).
  const visibleDocuments = (travel?.documents ?? [])
    .filter(doc => !HIDDEN_DOC_TYPES.includes(doc.type))
    .sort((a, b) => DOC_ORDER.indexOf(a.type) - DOC_ORDER.indexOf(b.type))

  const handleDownloadJson = async () => {
    if (!travel) return

    try {
      const res = await travelApi.getTravelExportJson(travel.id)
      downloadJson(`viaje-${travel.id}.json`, res.data)
    } catch (err) {
      console.debug('Error exporting travel JSON:', err)
      setError('Error al descargar el JSON del viaje')
    }
  }

  if (loading) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <BodyText>Cargando detalles del viaje...</BodyText>
        </View>
      </AppBackground>
    )
  }

  if (error) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <BodyText style={{ textAlign: 'center', marginBottom: 16, color: colors.rojoInactivo }}>
            {error}
          </BodyText>
          <TouchableOpacity
            onPress={() => navigate('/aena/travel')}
            style={{
              paddingVertical: 12,
              paddingHorizontal: 20,
              borderRadius: 8,
              backgroundColor: colors.aenaVerde,
              alignItems: 'center'
            }}
          >
            <BodyText style={{ color: colors.azulCorporativo, fontWeight: '600' }}>Volver a la lista</BodyText>
          </TouchableOpacity>
        </View>
      </AppBackground>
    )
  }

  if (!travel) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <BodyText>Viaje no encontrado</BodyText>
        </View>
      </AppBackground>
    )
  }

  if (showDeleteConfirm) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <View style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 24,
            alignItems: 'center',
            minWidth: 300,
            maxWidth: 400
          }}>
            <Headline2 style={{ marginBottom: 16, textAlign: 'center', color: colors.rojoInactivo }}>
              Confirmar eliminación
            </Headline2>

            <BodyText style={{ textAlign: 'center', marginBottom: 24, color: colors.grisOscuro }}>
              ¿Estás seguro de que quieres eliminar este viaje? Esta acción no se puede deshacer.
            </BodyText>

            <View style={{ flexDirection: 'row', gap: 12, width: '100%' }}>
              <TouchableOpacity
                onPress={() => setShowDeleteConfirm(false)}
                style={{
                  flex: 1,
                  paddingVertical: 12,
                  paddingHorizontal: 20,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: colors.grisMedio,
                  alignItems: 'center'
                }}
              >
                <BodyText style={{ color: colors.grisOscuro }}>Cancelar</BodyText>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={confirmDelete}
                style={{
                  flex: 1,
                  paddingVertical: 12,
                  paddingHorizontal: 20,
                  borderRadius: 8,
                  backgroundColor: colors.rojoInactivo,
                  alignItems: 'center'
                }}
              >
                <BodyText style={{ color: colors.blanco, fontWeight: '600' }}>Eliminar</BodyText>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </AppBackground>
    )
  }

  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>

        <HeaderWithBackAena
          title={`${travel.departureAirport.split(' ')[0]} ${travel.arrivalAirport.split(' ')[0]}`}
        />

        <BottomActionContainer
          bottomAction={
            <Button
              title="Ver documentos"
              size="block"
              textType="cta_medium"
              borderRadius={5}
              buttonWrapperStyle={{ backgroundColor: colors.aenaVerde }}
              color="primary"
              onPress={handleViewDocuments}
            />
          }
        >
          <ScrollView style={{ padding: 16 }}>
            {/* Header con fecha y estado */}
            <View style={{
              backgroundColor: 'white',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3
            }}>
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16
              }}>
                <View style={{
                  backgroundColor: colors.aenaVerde,
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 16
                }}>
                  <Headline5 style={{ color: colors.blanco }}>
                    Fecha: {TravelLogic.formatDate(travel.flightDate)}
                  </Headline5>
                </View>

                <View style={{
                  backgroundColor: getStatusColor(travel.status),
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 12
                }}>
                  <BodyText style={{ color: colors.blanco, fontWeight: '600' }}>
                    {getStatusText(travel.status)}
                  </BodyText>
                </View>
              </View>

              {/* Acciones */}
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
                <TouchableOpacity
                  onPress={handleEdit}
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: colors.azulCorporativo,
                    backgroundColor: colors.grisClaro
                  }}
                >
                  <BodyText style={{ color: colors.azulCorporativo, fontWeight: '600' }}>Editar</BodyText>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={handleDelete}
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: colors.rojoInactivo,
                    backgroundColor: colors.grisClaro
                  }}
                >
                  <BodyText style={{ color: colors.rojoInactivo, fontWeight: '600' }}>Eliminar</BodyText>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={handleDownloadJson}
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: colors.verdeActivo,
                    backgroundColor: colors.grisClaro
                  }}
                >
                  <BodyText style={{ color: colors.verdeActivo, fontWeight: '600' }}>Descargar JSON</BodyText>
                </TouchableOpacity>
              </View>
            </View>

            {/* Información del vuelo */}
            <View style={{
              backgroundColor: 'white',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3
            }}>
              <Headline5 style={{ marginBottom: 12, color: colors.azulCorporativo }}>
                Información del Vuelo
              </Headline5>

              <InfoRow label="Compañía" value={travel.airline} />
              <InfoRow label="Nº de vuelo" value={travel.flightNumber} />
              <InfoRow label="Localizador" value={travel.locator} />
              <InfoRow label="A.Salida" value={travel.departureAirport} />
              <InfoRow label="A.Destino" value={travel.arrivalAirport} />
            </View>

            {/* Documentos del viaje (descargo F244, certificado y certificados
                especiales/del perro). Pasaporte y vacunación se ocultan: su info
                ya va dentro del Certificado PetPass. Visible siempre (no solo
                cuando está validado). */}
            {visibleDocuments.length > 0 && (
              <View style={{
                backgroundColor: 'white',
                borderRadius: 12,
                padding: 16,
                marginBottom: 16,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3
              }}>
                <Headline5 style={{ marginBottom: 12, color: colors.azulCorporativo }}>
                  Documentos
                </Headline5>

                {visibleDocuments
                  .map((doc, i) => (
                    <View
                      key={`${doc.type}-${i}`}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        paddingVertical: 10,
                        borderBottomWidth: i < (visibleDocuments.length - 1) ? 1 : 0,
                        borderBottomColor: colors.grisClaro,
                      }}
                    >
                      <View style={{ flex: 1 }}>
                        <BodyText style={{ fontWeight: '600', color: colors.azulCorporativo, paddingLeft: 0 }}>
                          {docLabel(doc.type)}
                        </BodyText>
                        {!doc.url && (
                          <BodyText style={{ fontSize: 12, color: colors.grisOscuro, paddingLeft: 0 }}>
                            No disponible
                          </BodyText>
                        )}
                      </View>
                      {!!doc.url && (
                        <View style={{ flexDirection: 'row', gap: 8 }}>
                          <TouchableOpacity
                            onPress={() => openStoredDocument(doc.url)}
                            style={{
                              paddingVertical: 8,
                              paddingHorizontal: 14,
                              borderRadius: 8,
                              backgroundColor: colors.aenaVerde,
                            }}
                          >
                            <BodyText style={{ color: colors.blanco, fontWeight: '700', paddingLeft: 0 }}>Abrir</BodyText>
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() => downloadDocument(doc.url, `${doc.type}-${travel.flightNumber}`)}
                            style={{
                              paddingVertical: 8,
                              paddingHorizontal: 14,
                              borderRadius: 8,
                              borderWidth: 1.5,
                              borderColor: colors.verdeActivo,
                            }}
                          >
                            <BodyText style={{ color: colors.verdeActivo, fontWeight: '700', paddingLeft: 0 }}>Descargar</BodyText>
                          </TouchableOpacity>
                        </View>
                      )}
                    </View>
                  ))}
              </View>
            )}

            {/* Pasajeros (Mascotas) */}
            <View style={{
              backgroundColor: 'white',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3
            }}>
              <Headline5 style={{ marginBottom: 12, color: colors.azulCorporativo }}>
                Pasajeros
              </Headline5>

              {travel.pets.map(pet => (
                <PetCard key={pet.guid} pet={pet} />
              ))}
            </View>

            {/* Información del propietario */}
            <View style={{
              backgroundColor: 'white',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3
            }}>
              <Headline5 style={{ marginBottom: 12, color: colors.azulCorporativo }}>
                Información del Propietario
              </Headline5>

              <InfoRow label="Nombre" value={travel.ownerName} />
              <InfoRow label="Teléfono" value={travel.phoneNumber} />
              <InfoRow label="Email" value={travel.contactEmail} />
            </View>
          </ScrollView>
        </BottomActionContainer>
      </View>
    </AppBackground>
  )
}

export default TravelDetail
